/**
 * Cheaper per-frame VRM update for the toon renderer's heroes (same result as `vrm.update`).
 *
 * three-vrm's `VRMSpringBoneManager.update` re-derives each joint's dependencies every frame:
 * per joint it builds Sets, walks every ancestor of the joint's parent and of every collider
 * from the scene root, and allocates arrays doing it. With ~100 joints per VRoid model that was
 * the single biggest CPU cost of a frame (≈20% of the main thread with 4 heroes) and most of the
 * garbage. `FastSprings` computes the same order once and then per frame:
 *   1. refreshes the world matrices of the joints' ancestors and colliders once (parent first),
 *   2. runs each joint in dependency order (the manager's own `_processSpringBone` order),
 *   3. refreshes the non-joint descendants of each joint right after it (the manager's
 *      `traverseChildrenUntilConditionMet` step).
 * `VrmUpdater` adds a rate cap for the spring simulation (default 60 Hz, staggered between
 * heroes, so a 120 Hz display doesn't double the physics), skips expression updates while no
 * expression weight changed, and updates MToon materials only when they animate UVs.
 */
import type * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';

/** The parts of VRMSpringBoneJoint / VRMSpringBoneManager used here (private API checked at runtime). */
interface Joint {
  bone: THREE.Object3D;
  colliderGroups: { colliders: THREE.Object3D[] }[];
  update(delta: number): void;
}
interface SpringManager {
  joints: Set<Joint>;
  update(delta: number): void;
  reset(): void;
  _processSpringBone?: (j: Joint, tried: Set<Joint>, done: Set<Joint>, updated: Set<THREE.Object3D>, cb: (j: Joint) => void) => void;
}

export class FastSprings {
  /** Joints in dependency order. */
  readonly order: Joint[] = [];
  /** Non-joint descendants to refresh right after each joint (parallel to `order`). */
  private readonly after: THREE.Object3D[][] = [];
  /** Ancestors (inside the VRM scene) of every joint parent and collider, parent first, minus joints. */
  private readonly deps: THREE.Object3D[] = [];
  private readonly chain: THREE.Object3D[] = [];

  private constructor(private readonly root: THREE.Object3D) {}

  /** Null when the manager's internals aren't what we expect (callers fall back to `vrm.update`). */
  static build(vrm: VRM): FastSprings | null {
    const mgr = vrm.springBoneManager as unknown as SpringManager | null | undefined;
    if (!mgr || typeof mgr._processSpringBone !== 'function' || !(mgr.joints instanceof Set)) return null;
    const fs = new FastSprings(vrm.scene);
    const tried = new Set<Joint>();
    const done = new Set<Joint>();
    const updated = new Set<THREE.Object3D>();
    try {
      for (const j of mgr.joints) mgr._processSpringBone(j, tried, done, updated, (x) => fs.order.push(x));
    } catch {
      return null;
    }
    const jointBones = new Set(fs.order.map((j) => j.bone));
    for (const j of fs.order) {
      const list: THREE.Object3D[] = [];
      const walk = (o: THREE.Object3D): void => {
        for (const c of o.children) {
          if (jointBones.has(c)) continue;
          list.push(c);
          walk(c);
        }
      };
      walk(j.bone);
      fs.after.push(list);
    }
    // Ancestors inside the VRM scene of every dependency, parent first, skipping joint bones
    // (those are recomputed in order below).
    const depSet = new Set<THREE.Object3D>();
    const addChain = (o: THREE.Object3D | null): void => {
      const up: THREE.Object3D[] = [];
      for (let x = o; x && x !== vrm.scene; x = x.parent) up.push(x);
      for (let i = up.length - 1; i >= 0; i--) {
        const x = up[i]!;
        if (jointBones.has(x) || depSet.has(x)) continue;
        depSet.add(x);
        fs.deps.push(x);
      }
    };
    for (const j of fs.order) {
      addChain(j.bone.parent);
      for (const g of j.colliderGroups) for (const c of g.colliders) addChain(c);
    }
    // depSet insertion keeps each chain parent-first, but a later chain may add an ancestor of an
    // earlier entry only if that ancestor was missing; it never is (chains start at the root).
    return fs;
  }

  /** Run every joint once with `delta` seconds. */
  update(delta: number): void {
    // World matrices above the VRM scene (unit root, body, fall pivot), root first.
    const chain = this.chain;
    chain.length = 0;
    for (let x: THREE.Object3D | null = this.root; x; x = x.parent) chain.push(x);
    for (let i = chain.length - 1; i >= 0; i--) chain[i]!.updateWorldMatrix(false, false);
    for (const o of this.deps) o.updateWorldMatrix(false, false);
    const order = this.order;
    for (let i = 0; i < order.length; i++) {
      const j = order[i]!;
      const b = j.bone;
      b.updateMatrix();
      b.updateWorldMatrix(false, false);
      j.update(delta);
      const after = this.after[i]!;
      for (let k = 0; k < after.length; k++) after[k]!.updateWorldMatrix(false, false);
    }
  }
}

interface ExprManager {
  update(): void;
  setValue(name: string, w: number): void;
  getValue?(name: string): number | null;
}

/** Spring-bone simulation rate cap (Hz) by quality; 0 = off (bones keep their rest pose). */
export type SpringHz = number;

export class VrmUpdater {
  private springs: FastSprings | null;
  private springAcc: number;
  private first = true;
  private readonly animatedMats: { update(d: number): void }[] = [];
  private lastBlink = -1;
  /** Max spring simulation rate (Hz). */
  springHz: SpringHz = 60;
  /**
   * Non-joint objects attached under humanoid bones (weapon holders). FastSprings only refreshes
   * joints, their ancestors and their descendants, so these get their world matrices (and their
   * children's) refreshed here after the pose and springs are applied: a few matrix multiplies.
   */
  followers: readonly THREE.Object3D[] = [];

  /** `phase` 0..1 staggers the spring steps of several heroes across frames. */
  constructor(private readonly vrm: VRM, phase = 0) {
    this.springs = FastSprings.build(vrm);
    this.springAcc = phase / 60;
    for (const m of (vrm.materials ?? []) as unknown as Record<string, unknown>[]) {
      const sx = Number(m['uvAnimationScrollXSpeedFactor'] ?? 0);
      const sy = Number(m['uvAnimationScrollYSpeedFactor'] ?? 0);
      const rot = Number(m['uvAnimationRotationSpeedFactor'] ?? 0);
      if ((sx || sy || rot) && typeof m['update'] === 'function') this.animatedMats.push(m as unknown as { update(d: number): void });
    }
  }

  /** Blink weight 0..1 (only pushed to the meshes when it changed). */
  setBlink(w: number): void {
    const em = this.vrm.expressionManager as unknown as ExprManager | null;
    if (!em) return;
    const q = Math.round(w * 50) / 50;
    if (q === this.lastBlink) return;
    this.lastBlink = q;
    em.setValue('blink', q);
    em.update();
  }

  reset(): void {
    (this.vrm.springBoneManager as unknown as SpringManager | null)?.reset();
  }

  update(dtS: number): void {
    const vrm = this.vrm;
    if (this.first || !this.springs) {
      // First frame (and the fallback path): the stock update, materials included.
      this.first = false;
      vrm.update(dtS);
      this.updateFollowers();
      return;
    }
    vrm.humanoid.update();
    (vrm as unknown as { nodeConstraintManager?: { update(): void } }).nodeConstraintManager?.update();
    for (const m of this.animatedMats) m.update(dtS);
    if (this.springHz > 0) {
      const step = 1 / this.springHz;
      this.springAcc += dtS;
      // Run once 3/4 of a step has built up (a 60 Hz display runs every frame despite jitter, a
      // 120 Hz one every other frame), integrating all the time since the last step.
      if (this.springAcc >= 0.75 * step) {
        const d = Math.min(this.springAcc, 0.05);
        this.springAcc = 0;
        this.springs.update(d);
      }
    }
    this.updateFollowers();
  }

  /** World matrices of the followers (ancestors first, then their subtrees). */
  private updateFollowers(): void {
    const f = this.followers;
    for (let i = 0; i < f.length; i++) f[i]!.updateWorldMatrix(true, true);
  }
}
